import * as React from 'react'
import { useParams } from 'react-router-dom'

import { PusherEvent, usePusherEvent } from '~publish/services/pusher'

import { useIsCommunityRoute } from '../navigation/useIsCommunityRoute'
import { registerIngestedEngagementId } from '../engagement/newEngagementSignal'

type UseNewMentionsAvailableButtonResponse = {
  showNewMentionsButton: boolean
  hideNewMentionsButton: () => void
}

type UseNewMentionsAvailableButtonProps = {
  channelId?: string
}

/** `mentionId` matches the `id` the GraphQL layer returns for the mention. */
type MentionIngestedEventData = {
  channelId?: string
  mentionId?: string
}

/**
 * Mentions counterpart to {@link useNewCommentsAvailableButton}: tracks whether
 * the "new mentions available" pill should show, driven by the
 * MENTION_INGESTED Pusher event. There is no backfill-completion event for
 * mentions, so this hook subscribes to a single event.
 *
 * The mentions inbox has no by-post view, so there is no post-scoped matching —
 * only channel matching (omitted in the all-channels view, where `channelId` is
 * undefined and every channel's events count).
 */
export const useNewMentionsAvailableButton = ({
  channelId: propChannelId,
}: UseNewMentionsAvailableButtonProps = {}): UseNewMentionsAvailableButtonResponse => {
  const [showNewMentionsButton, setShowNewMentionsButton] =
    React.useState(false)

  const hideNewMentionsButton = React.useCallback(() => {
    setShowNewMentionsButton(false)
  }, [])

  const isCommunityRoute = useIsCommunityRoute()

  // Extract channelId from URL params when on a channel-specific route.
  const { channelId: urlChannelId } = useParams<{ channelId: string }>()

  // Use prop channelId if provided, otherwise use URL channelId.
  const channelId = propChannelId || urlChannelId

  usePusherEvent(
    PusherEvent.MENTION_INGESTED,
    (data: MentionIngestedEventData): void => {
      if (!isCommunityRoute) {
        setShowNewMentionsButton(false)
        return
      }

      const channelMatches =
        !channelId || // Show if no specific channel filter
        !data.channelId || // Show if event has no channel info
        channelId === data.channelId // Show if channels match

      setShowNewMentionsButton(channelMatches)
      if (channelMatches && data.mentionId) {
        registerIngestedEngagementId('mention', data.mentionId)
      }
    },
  )

  return { showNewMentionsButton, hideNewMentionsButton }
}
